import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { portfolioItems } from '../data/cinematicContent';

const AUTO_SLIDE_MS = 5200;

export const CinematicHero = () => {
  const slides = useMemo(() => portfolioItems.slice(0, 7), []);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    if (slides.length < 2) {
      return;
    }

    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, AUTO_SLIDE_MS);

    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen overflow-hidden bg-[#e3ebf3] pt-24 md:pt-28">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_12%,rgba(112,141,170,0.38),transparent_36%),radial-gradient(circle_at_90%_80%,rgba(86,114,142,0.26),transparent_42%)]" />
      <div className="hero-strata absolute inset-0 opacity-30" />

      <div className="relative z-10 mx-auto flex min-h-[calc(100vh-6.5rem)] w-full max-w-[1280px] items-center px-4 pb-10 sm:px-6 md:pb-12">
        <div className="w-full overflow-hidden rounded-[34px] border border-slate-300/80 bg-white/35 shadow-[0_30px_90px_rgba(71,94,120,0.24)] backdrop-blur-sm">
          <div className="relative aspect-[1/1] sm:aspect-[4/3] md:aspect-[5/4] lg:aspect-[16/10] bg-gradient-to-b from-slate-600/20 via-slate-500/10 to-slate-700/20">
            {slides.map((slide, index) => (
              <article
                key={slide.id}
                className={`absolute inset-0 transition-all duration-700 ${
                  index === activeSlide
                    ? 'translate-x-0 opacity-100'
                    : index < activeSlide
                      ? '-translate-x-6 opacity-0'
                      : 'translate-x-6 opacity-0'
                }`}
                aria-hidden={index !== activeSlide}
              >
                <img
                  src={slide.image}
                  alt={slide.title}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  className="relative z-[1] h-full w-full object-cover object-center"
                />
                <div className="absolute inset-x-0 bottom-0 h-[45%] bg-gradient-to-t from-black/50 via-black/22 to-transparent" />

                <div className="absolute bottom-0 left-0 z-[3] w-full p-6 md:p-8">
                  <div className="w-full sm:max-w-[72%] md:max-w-[58%] rounded-2xl border border-white/16 bg-black/45 px-4 py-3 shadow-[0_12px_30px_rgba(0,0,0,0.35)] backdrop-blur-[14px] sm:px-5 sm:py-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-200/80">Previous Work</p>
                    <h2 className="mt-2 text-xl sm:text-2xl font-semibold text-[#cc1a1a] md:text-4xl leading-tight">
                      {slide.title}
                    </h2>
                    <p className="hidden sm:block mt-3 text-sm text-slate-100 md:text-base line-clamp-2">{slide.description}</p>
                    <div className="mt-3 sm:mt-4 flex items-center gap-2 text-xs sm:text-sm font-medium text-slate-100 truncate">
                      <MapPin className="h-4 w-4" />
                      {slide.location}
                    </div>
                  </div>
                </div>
              </article>
            ))}

            <button
              onClick={prevSlide}
              aria-label="Previous slide"
              className="absolute left-3 sm:left-4 top-[46%] sm:top-1/2 flex h-10 w-10 sm:h-11 sm:w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/38 text-white shadow-lg transition hover:bg-black/52"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={nextSlide}
              aria-label="Next slide"
              className="absolute right-3 sm:right-4 top-[46%] sm:top-1/2 flex h-10 w-10 sm:h-11 sm:w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/38 text-white shadow-lg transition hover:bg-black/52"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-slate-200 bg-white/90 px-5 py-4 md:px-8 md:py-5">
            <div className="flex items-center gap-2">
              {slides.map((slide, index) => (
                <button
                  key={slide.id}
                  aria-label={`Go to slide ${index + 1}`}
                  onClick={() => setActiveSlide(index)}
                  className={`h-2.5 rounded-full transition-all ${
                    index === activeSlide ? 'w-11 bg-slate-600' : 'w-2.5 bg-slate-300 hover:bg-slate-500'
                  }`}
                />
              ))}
            </div>

            <Button
              onClick={() => scrollToSection('#products')}
              className="bg-slate-700 text-white hover:bg-slate-600"
            >
              Explore Product Lines
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
